import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import LazyLoading from '../../../components/LazyLoading';
import { SalesOrderDataForm, defaultSalesOrderDataForm } from '../../../app/models/SalesOrder';
import useSalesOrderService from '../../../app/services/hooks/useSalesOrderService';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import useFetch from '../../../hooks/useFetch';
import useNavigationPage from '../../../hooks/useNavigationPage';
import { EndPoints } from '../../../app/services/EndPoints';
import { Society } from '../../../app/models/Society';
import { SocietyClientDataForm } from '../../../app/models/SocietyClient';
import { Currency } from '../../../app/models/Currency';
import { PaymentMethod } from '../../../app/models/PaymentMethod';
import { PaymentCondition } from '../../../app/models/PaymentCondition';
import { Sector } from '../../../app/models/Sector';
import { Location } from '../../../app/models/Location';
import { Incoterm } from '../../../app/models/Incoterm';

import SalesOrderForm from './forms/SalesOrderForm';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';

interface Props {
    salesOrderId: number;
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
    onSalesOrderChange?: (salesOrder: SalesOrderDataForm) => void;
}

const SalesOrderEdit = ({ salesOrderId, onSaved, onCancel, onError, onSalesOrderChange }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();
    const { editSalesOrder, updateSalesOrder, fetchingEditSalesOrder, fetchingUpdateSalesOrder } =
        useSalesOrderService();
    const { doGet } = useFetch();
    const { getClientsBySociety } = useSalesOrderService();

    const [salesOrder, setSalesOrder] = useState<SalesOrderDataForm>(defaultSalesOrderDataForm);
    const [errorFields, setErrorFields] = useState<any>();
    const [societies, setSocieties] = useState<Society[]>([]);
    const [societyClients, setSocietyClients] = useState<SocietyClientDataForm[]>([]);
    const [clientContacts, setClientContacts] = useState<any[]>([]);
    const [sectors, setSectors] = useState<Sector[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [paymentConditions, setPaymentConditions] = useState<PaymentCondition[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [incoterms, setIncoterms] = useState<Incoterm[]>([]);
    const [loadingData, setLoadingData] = useState(true);

    useEffect(() => {
        loadInitialData();
        edit();
    }, []);

    // Cargar clientes cuando se seleccione una sociedad
    useEffect(() => {
        if (salesOrder.society_id) {
            getClientsBySociety(salesOrder.society_id, {
                onSuccess: (response: ServiceResponse) => {
                    const clientsData = response.data.society_clients || [];
                    setSocietyClients(clientsData);
                },
                onError: (response: ServiceResponse) => {
                    toast.error(response.message);
                    setSocietyClients([]);
                }
            });
        } else {
            setSocietyClients([]);
        }
    }, [salesOrder.society_id]);

    // Cargar contactos cuando se seleccione un cliente
    useEffect(() => {
        if (salesOrder.client_id) {
            doGet({
                url: EndPoints.BUSINESS_NAME_CONTACTS.GET_BUSINESS_NAME_CONTACT_BY_CLIENT.replace(':client_id', salesOrder.client_id.toString()),
                onSuccess: (response: ServiceResponse) => {
                    const contactsData = response.data.client_contacts || [];
                    setClientContacts(contactsData);
                },
                onError: (response: ServiceResponse) => {
                    toast.error(response.message);
                    setClientContacts([]);
                }
            });
        } else {
            setClientContacts([]);
        }
    }, [salesOrder.client_id]);

    const loadInitialData = () => {
        // Cargar todos los datos iniciales desde el endpoint create
        doGet({
            url: EndPoints.SALES_ORDERS.CREATE_SALES_ORDER,
            onSuccess: (response: ServiceResponse) => {
                setSocieties(response.data.societies || []);
                setSectors(response.data.sectors || []);
                setIncoterms(response.data.incoterms || []);
                setLocations(response.data.locations || []);
                setCurrencies(response.data.currencies || []);
                setPaymentMethods(response.data.payment_methods || []);
                setPaymentConditions(response.data.payment_conditions || []);
                setLoadingData(false);
            },
            onError: (errorResponse: ServiceResponse) => {
                console.error('Error loading initial data:', errorResponse);
                toast.error(errorResponse.message || 'Error al cargar los datos iniciales');
                setLoadingData(false);
            }
        });
    };

    const edit = () => {
        if (showLoading) showLoading('loading', 'Cargando orden de venta...');
        editSalesOrder(salesOrderId, {
            onSuccess: (response: ServiceResponse) => {
                const orderData = response.data.sales_order;
                setSalesOrder(orderData);
                if (onSalesOrderChange) {
                    onSalesOrderChange(orderData);
                }
                if (hideLoading) hideLoading();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                if (onError) onError();
            }
        });
    };

    const update = () => {
        if (showLoading) showLoading('loading', 'Actualizando orden de venta...');
        updateSalesOrder(salesOrderId, salesOrder, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                toast.success(response.message, { autoClose: 2500 });
                setErrorFields(undefined);
                edit(); // Esto recargará los datos y actualizará onSalesOrderChange
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) {
            onCancel();
        } else {
            navigationPage('/sales-orders');
        }
    };

    if (fetchingEditSalesOrder || loadingData) {
        return <LazyLoading height="300" />;
    }

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <SalesOrderForm
                        salesOrder={salesOrder}
                        setSalesOrder={(newOrder) => {
                            setSalesOrder(newOrder);
                            if (onSalesOrderChange) {
                                onSalesOrderChange(newOrder);
                            }
                        }}
                        societies={societies}
                        societyClients={societyClients}
                        clientContacts={clientContacts}
                        sectors={sectors}
                        currencies={currencies}
                        paymentMethods={paymentMethods}
                        paymentConditions={paymentConditions}
                        locations={locations}
                        incoterms={incoterms}
                        errorFields={errorFields}
                    />
                </div>
            </div>

            <hr />

            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm callbackCancel={cancel} locked={fetchingUpdateSalesOrder} />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm callbackSave={update} locked={fetchingUpdateSalesOrder} />
                </div>
            </div>
        </>
    );
};

export default SalesOrderEdit;
